/**
 * MenuPreviewBackdrop — a non-interactive render of the family menu, used
 * as the dimmed backdrop behind the forced Create Account modal on the
 * public "Get started" funnel (Airbnb-style "half-see the menu, sign up to
 * use it").
 *
 * It mirrors the real app chrome — `@ordo/ui` app-shell + a static parent
 * sidebar (Orders / My Family / Billing; no Help or account footer, since
 * there's no signed-in user yet) — and lays out "This Week's Menu" as
 * day-grouped image cards per the approved mockup.
 *
 * The data is intentionally STATIC sample data: a teaser behind a dimming
 * overlay, never a live menu, so it needs no auth and no menu endpoint. The
 * food photos are static assets in `public/menu-preview/` (these are fixed
 * marketing images — not user uploads — so they ship with the app rather
 * than living on S3). The whole tree is `aria-hidden` + `pointer-events-none`
 * so it can't be read or clicked through the modal.
 */

import { AppShell, AppShellContent, AppShellMain } from '@ordo/ui/app-shell'
import { AppSidebar, AppSidebarBrand, AppSidebarItem, AppSidebarNav, AppSidebarSection } from '@ordo/ui/app-sidebar'
import { Input } from '@ordo/ui/input'
import { OrdoLogo } from '@ordo/ui/logos'
import { Calendar, Plus, Receipt, Search, Users } from 'lucide-react'

interface PreviewItem {
  img: string
  title: string
  side: string
  veg?: boolean
  dairyFree?: boolean
}

// The 8 menu items, keyed by their static asset filename in
// `public/menu-preview/`. Titles + sides + dietary flags match the mockup.
const ITEMS = {
  pork: { img: 'bbq-pulled-pork-sando.jpg', title: 'BBQ Pulled Pork Sando', side: 'Roasted Potato Wedges' },
  quesadilla: { img: 'cheese-quesadilla.jpg', title: 'Cheese Quesadilla', side: 'Roasted Potato Wedges', veg: true },
  sub: { img: 'turkey-cheddar-sub.jpg', title: 'Turkey & Cheddar Sub', side: 'Fuji Apple' },
  caesar: { img: 'grilled-chicken-caesar-salad.jpg', title: 'Chicken Caesar Salad', side: 'Fuji Apple' },
  tenders: {
    img: 'wholegrain-chicken-tenders.jpg',
    title: 'Wholegrain Chicken Tenders',
    side: 'Steamed Broccoli',
    dairyFree: true,
  },
  drumstick: { img: 'chicken-drumstick-roll.jpg', title: 'Chicken Drumstick & Roll', side: 'Steamed Green Beans' },
  grilledCheese: {
    img: 'grilled-cheddar-cheese-sando.jpg',
    title: 'Grilled Cheese',
    side: 'Steamed Green Beans',
    veg: true,
  },
  mac: { img: 'mac-and-cheese.jpg', title: 'Mac & Cheese', side: 'Steamed Broccoli', veg: true },
} satisfies Record<string, PreviewItem>

// Monday + Tuesday match the mockup exactly; the rest of the week reuses the
// same pool so the teaser reads like a full, varied week.
const WEEK: Array<{ day: string; items: PreviewItem[] }> = [
  { day: 'Monday', items: [ITEMS.pork, ITEMS.quesadilla, ITEMS.sub, ITEMS.caesar, ITEMS.tenders] },
  { day: 'Tuesday', items: [ITEMS.drumstick, ITEMS.grilledCheese, ITEMS.sub, ITEMS.caesar, ITEMS.mac] },
  { day: 'Wednesday', items: [ITEMS.quesadilla, ITEMS.tenders, ITEMS.sub, ITEMS.caesar, ITEMS.pork] },
  { day: 'Thursday', items: [ITEMS.grilledCheese, ITEMS.mac, ITEMS.sub, ITEMS.caesar, ITEMS.drumstick] },
  { day: 'Friday', items: [ITEMS.tenders, ITEMS.pork, ITEMS.sub, ITEMS.caesar, ITEMS.mac] },
]

const FILTERS = ['Dairy free', 'Gluten free', 'Vegetarian']

function StaticParentSidebar() {
  return (
    <AppSidebar variant="desktop" style={{ fontFamily: 'var(--font-sans)' }}>
      <AppSidebarBrand>
        <span className="px-2">
          <OrdoLogo className="h-8 w-auto" />
        </span>
      </AppSidebarBrand>
      <AppSidebarNav>
        <AppSidebarSection>
          <AppSidebarItem active icon={<Calendar className="h-5 w-5 md:h-4 md:w-4" />}>
            Orders
          </AppSidebarItem>
          <AppSidebarItem icon={<Users className="h-5 w-5 md:h-4 md:w-4" />}>My Family</AppSidebarItem>
          <AppSidebarItem icon={<Receipt className="h-5 w-5 md:h-4 md:w-4" />}>Billing</AppSidebarItem>
        </AppSidebarSection>
      </AppSidebarNav>
    </AppSidebar>
  )
}

function DietBadge({ kind }: { kind: 'veg' | 'dairyFree' }) {
  if (kind === 'veg') {
    return (
      <span className="grid size-5 place-items-center rounded-full bg-green-100 text-[10px] font-bold text-green-700">
        V
      </span>
    )
  }
  return (
    <span className="grid size-5 place-items-center rounded-full bg-blue-100 text-[9px] font-bold text-blue-700">
      DF
    </span>
  )
}

function MenuCard({ item }: { item: PreviewItem }) {
  return (
    <div className="flex flex-col gap-2">
      {/* The labeled placeholder SVG is painted as the tile background so
          something always renders. The real photo (/menu-preview/<name>.jpg)
          overlays it when present; if it's missing, the <img> errors and
          hides itself, revealing the placeholder underneath. Drop the .jpg
          and it takes over — no code change. */}
      <div
        className="relative aspect-square overflow-hidden rounded-2xl bg-muted bg-cover bg-center"
        style={{ backgroundImage: `url(/menu-preview/placeholder/${item.img.replace(/\.[^.]+$/, '.svg')})` }}>
        <img
          src={`/menu-preview/${item.img}`}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <span className="absolute bottom-2 right-2 grid size-9 place-items-center rounded-full bg-background text-foreground shadow-md">
          <Plus className="size-5" />
        </span>
      </div>
      <p className="text-sm leading-snug">
        <span className="font-semibold">{item.title}</span> with {item.side}
      </p>
      {item.veg || item.dairyFree ? (
        <div className="flex gap-1">
          {item.veg ? <DietBadge kind="veg" /> : null}
          {item.dairyFree ? <DietBadge kind="dairyFree" /> : null}
        </div>
      ) : null}
    </div>
  )
}

export function MenuPreviewBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none select-none">
      <AppShell pathname="/orders" className="text-foreground">
        <StaticParentSidebar />
        <AppShellContent>
          <AppShellMain className="overflow-y-auto">
            <div className="mx-auto w-full max-w-6xl p-6 md:p-8">
              <header className="mb-6 border-b pb-4">
                <h1 className="text-2xl font-semibold tracking-tight">Menu</h1>
              </header>

              {/* Search + dietary filters row. */}
              <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                <div className="relative w-full max-w-xl">
                  <Input placeholder="Search items" className="pr-10" readOnly tabIndex={-1} />
                  <Search className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Filters:</span>
                  {FILTERS.map((f) => (
                    <span key={f} className="rounded-full bg-muted px-3 py-1 text-sm">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* Day-grouped menu cards. */}
              <div className="flex flex-col gap-8">
                {WEEK.map(({ day, items }) => (
                  <section key={day}>
                    <h2 className="mb-3 text-lg font-semibold">{day}</h2>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                      {items.map((item, i) => (
                        <MenuCard key={`${day}-${i}`} item={item} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </AppShellMain>
        </AppShellContent>
      </AppShell>
    </div>
  )
}
